import { useEffect, useRef, useState, type ReactNode } from 'react';
import { pickLocale, type Component, type Group } from '../schema';
import { useTranslation } from '../i18n';
import { useConfigentoStoreRef, useSetValidationErrors } from '../state/StoreContext';
import { isGroupVisible, useRuleResults } from '../rules/RuleResultsContext';
import { CharacteristicSlot, GroupBlock } from './GroupBlock';
import { findCharacteristicElement } from './findCharacteristicElement';
import { validateCharacteristics, type ValidationError } from '../validation/validate';
import type { RuleError, RuleResult } from '../rules/types';
import type { WizardNav } from '../api/webshopConfig';
import type { GroupRef, StepDirection, StepGuardErrors, StepGuardResult } from '../api/groupNavigation';
import { getInstanceEventListeners, getStepGuard, publicApi, setGroupNavigator } from '../public-api';

interface GroupNavigatorRendered {
    nav: ReactNode | null;
    main: ReactNode;
}

/**
 * Group-rendering dispatcher driven by `component.layout`:
 *
 * - `no_groups` → all groups stacked vertically, no nav chrome
 * - `tabs`      → horizontal tab strip, one group visible at a time
 * - `wizard`    → progress indicator + current group + prev/next buttons
 *
 * Returns the nav and main slots so {@link Shell} can mount them in their
 * respective positions (nav under the header, main in the content area).
 *
 * This is a hook-style helper (not a component) because it owns the
 * group-selection useState — callers must invoke it during their own render.
 */
export function useGroupNavigator(
    component: Component,
    locale: string | undefined,
    opts: {
        instanceId?: string | undefined;
        wizardNav?: WizardNav | undefined;
        /**
         * The current rule results. Passed in rather than read from context:
         * the root calls this hook above the RuleResultsProvider it renders,
         * so the context would hand back the empty default — rule-hidden
         * groups stayed in the tab strip and rule-hidden required fields
         * blocked Next.
         */
        ruleResults?: RuleResult | undefined;
    } = {},
): GroupNavigatorRendered {
    const { layout, groupWizard, groupWizardAutoscroll: autoscroll, groupScrollOffset: scrollOffset } = component;
    const { instanceId } = opts;
    // The embed's choice wins over the merchant's default in the Component.
    const wizardNav: WizardNav = opts.wizardNav ?? component.wizardNav ?? 'full';
    // Hooks are always called, even for empty / single-group layouts, to keep
    // the hook order stable across renders.
    //
    // The active group is held by id, not by position: rules can hide and
    // show groups, and a position would then silently point at another one.
    const [activeId, setActiveId] = useState<number | null>(null);
    const [pending, setPending] = useState(false);
    // Message of a wizard error rule without a Characteristic to sit at.
    const [stepMessage, setStepMessage] = useState<string | null>(null);
    const setValidationErrors = useSetValidationErrors();
    const store = useConfigentoStoreRef();
    const { t, locale: uiLocale } = useTranslation();
    const contextRules = useRuleResults();
    const ruleResults = opts.ruleResults ?? contextRules;
    const runGroupValidation = (group: Group): ValidationError[] =>
        validateCharacteristics(group.characteristics, store.getState().selections, ruleResults, t, uiLocale);

    // Filter to visible Groups only. Mirrors legacy `hideEmptyGroups`: a
    // Group disappears from tabs / wizard chrome when every Characteristic
    // in it is hidden (admin visibility, rule-blacklisted, or all Values
    // blacklisted).
    const groups = component.groups.filter((g) => isGroupVisible(g, ruleResults));
    const navigable = layout !== 'no_groups' && groups.length > 1;
    const safeIdx = navigable ? resolveActiveIndex(component.groups, groups, activeId) : 0;
    const active = navigable ? groups[safeIdx] : undefined;
    const isWizard = groupWizard || layout === 'wizard';

    const refOf = (g: Group, index: number): GroupRef => ({ id: g.id, key: g.altTitle ?? null, index });

    const showErrors = (errors: ValidationError[]): void => {
        setValidationErrors(errors);
        const firstId = errors[0]?.characteristicId;
        if (typeof firstId === 'number' && typeof document !== 'undefined') {
            findCharacteristicElement(firstId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    /**
     * The one path every step change takes — Next / Back buttons, tab clicks
     * and the host API. Forward: validate each step on the way (stop at the
     * first that fails), then ask the host's guard. Back: always allowed,
     * clears surfaced errors.
     */
    const navigate = async (
        targetIdx: number,
        how: { validate: boolean; from: EventTarget | null },
    ): Promise<boolean> => {
        if (!navigable || pending) return false;
        const target = groups[targetIdx];
        const current = groups[safeIdx];
        if (!target || !current) return false;
        if (targetIdx === safeIdx) return true;
        const forward = targetIdx > safeIdx;

        if (forward && how.validate) {
            for (let i = safeIdx; i < targetIdx; i++) {
                const step = groups[i];
                if (!step) continue;
                const errors = runGroupValidation(step);
                if (errors.length > 0) {
                    if (i !== safeIdx) setActiveId(step.id);
                    setStepMessage(null);
                    showErrors(errors);
                    return false;
                }
                // Legacy: an error rule set to run at the wizard holds the
                // customer on its step and shows its message.
                const blocking = wizardRuleErrors(component, ruleResults, step, groups, safeIdx);
                if (blocking.length > 0) {
                    if (i !== safeIdx) setActiveId(step.id);
                    const atField = blocking.flatMap((e) => ruleErrorAtField(component, e, uiLocale));
                    showErrors(atField);
                    const loose = blocking.filter((e) => !e.characteristic);
                    setStepMessage(loose.length > 0 ? loose.map((e) => messageText(e, uiLocale)).join(' ') : null);
                    return false;
                }
            }
        }

        const guard = instanceId ? getStepGuard(instanceId) : null;
        if (guard && (forward || guard.backward)) {
            const direction: StepDirection = Math.abs(targetIdx - safeIdx) === 1
                ? (forward ? 'forward' : 'back')
                : 'jump';
            setPending(true);
            let verdict: StepGuardResult;
            try {
                verdict = await guard.guard({
                    from: refOf(current, safeIdx),
                    to: refOf(target, targetIdx),
                    direction,
                });
            } catch (err) {
                // A broken guard must not trap the customer on this step.
                console.error('[Configento] step guard threw — allowing the step:', err);
                verdict = true;
            } finally {
                setPending(false);
            }
            if (verdict === false) return false;
            if (Array.isArray(verdict) && verdict.length > 0) {
                showErrors(guardErrors(component, verdict));
                return false;
            }
        }

        setValidationErrors([]);
        setStepMessage(null);
        setActiveId(target.id);
        if (autoscroll && isWizard) {
            const root = how.from ?? (instanceId ? publicApi.instances.get(instanceId)?.element ?? null : null);
            scrollWizardIntoView(root, scrollOffset);
        }
        return true;
    };

    // Latest closure for the host API; the registered controller object
    // stays the same across renders.
    const latest = useRef({ groups, safeIdx, navigable, navigate, locale });
    latest.current = { groups, safeIdx, navigable, navigate, locale };
    useEffect(() => {
        if (!instanceId) return;
        const indexOf = (ref: number | string): number =>
            latest.current.groups.findIndex((g) =>
                typeof ref === 'number' ? g.id === ref : (g.altTitle === ref || String(g.id) === ref));
        setGroupNavigator(instanceId, {
            getGroups: () => latest.current.groups.map((g, index) => ({
                id: g.id,
                key: g.altTitle ?? null,
                index,
                title: pickLocale(g.title, latest.current.locale) ?? '',
                characteristics: g.characteristics.flatMap((c) => (c.altTitle ? [c.altTitle] : [])),
            })),
            getActive: () => {
                const { groups: gs, safeIdx: idx, navigable: nav } = latest.current;
                const g = nav ? gs[idx] : undefined;
                return g ? { id: g.id, key: g.altTitle ?? null, index: idx, total: gs.length } : null;
            },
            goTo: (ref, o) => {
                const idx = indexOf(ref);
                return idx < 0 ? Promise.resolve(false) : latest.current.navigate(idx, { validate: o.validate, from: null });
            },
            next: () => latest.current.navigate(latest.current.safeIdx + 1, { validate: true, from: null }),
            prev: () => latest.current.navigate(latest.current.safeIdx - 1, { validate: false, from: null }),
        });
        return () => setGroupNavigator(instanceId, null);
    }, [instanceId]);

    // `group` event: after every change of the visible step, whatever caused
    // it (button, tab, host call, a rule hiding the current group). Not on
    // the first display.
    const shown = useRef<GroupRef | null>(null);
    useEffect(() => {
        if (!active) return;
        const to = refOf(active, safeIdx);
        const from = shown.current;
        shown.current = to;
        if (!from || from.id === to.id || !instanceId) return;
        const direction: StepDirection = to.index === from.index + 1
            ? 'forward'
            : to.index === from.index - 1 ? 'back' : 'jump';
        for (const cb of getInstanceEventListeners(instanceId)?.group ?? []) {
            try { cb({ from, to, direction }); }
            catch (err) { console.error('[Configento] group listener threw:', err); }
        }
    });

    if (groups.length === 0) {
        return { nav: null, main: <EmptyState /> };
    }

    if (layout === 'no_groups') {
        // Flatten ALL characteristics into one section — no per-group chrome,
        // no group titles. Matches the legacy storefront, which hides the tab
        // strip (the only place group titles live) when `groupLayout` is
        // `no_groups`. Visibility still runs per-Characteristic via the same
        // CharacteristicSlot gate used inside GroupBlock.
        return {
            nav: null,
            main: (
                <section className="cfg-group" data-configento-flat>
                    <div className="cfg-group__chars">
                        {groups.flatMap((g) =>
                            g.characteristics.map((char) => (
                                <CharacteristicSlot
                                    key={`${g.id}-${char.id}`}
                                    char={char}
                                    locale={locale}
                                    siblings={g.characteristics}
                                />
                            )),
                        )}
                    </div>
                </section>
            ),
        };
    }

    if (groups.length === 1) {
        return {
            nav: null,
            main: <GroupBlock group={groups[0]!} locale={locale} />,
        };
    }

    const body = active ? <GroupBlock group={active} locale={locale} showTitle={false} /> : <EmptyState />;
    const onTabSelect = (idx: number, from: EventTarget | null) => {
        // Wizard: jumping forward validates every step on the way, like Next.
        // Plain tabs: free navigation, but the host's guard still gets asked.
        void navigate(idx, { validate: isWizard, from });
    };
    const tabs = <TabsBar groups={groups} activeIdx={safeIdx} onSelect={onTabSelect} locale={locale} />;

    // Wizard chrome is gated on `groupWizard`, NOT on `layout`. Legacy data
    // typically combines `groupLayout: 'tabs'` + `groupWizard: '1'` to mean
    // "show tab strip but ALSO show progress + prev/next" — wizard layers on
    // top of tabs. `layout === 'wizard'` is the new-v3 simplification, both
    // routes through the same wizard renderer.
    if (isWizard) {
        return {
            nav: wizardNav === 'full' ? (
                <>
                    {tabs}
                    <WizardBar idx={safeIdx} total={groups.length} />
                </>
            ) : null,
            main: (
                <>
                    {body}
                    {stepMessage && (
                        <p className="cfg-wizard-message" role="alert">{stepMessage}</p>
                    )}
                    {wizardNav !== 'none' && (
                        <WizardActions
                            idx={safeIdx}
                            total={groups.length}
                            busy={pending}
                            onBack={(from) => void navigate(safeIdx - 1, { validate: false, from })}
                            onNext={(from) => void navigate(safeIdx + 1, { validate: true, from })}
                        />
                    )}
                </>
            ),
        };
    }

    // Plain tabs (no wizard chrome). `none` hands the strip to the host too.
    return { nav: wizardNav === 'none' ? null : tabs, main: body };
}

/**
 * Position of the active group among the visible ones. When the remembered
 * group is gone (a rule hid it), fall back to the closest visible group
 * before it — the customer stays where they were instead of being thrown
 * back to the start.
 */
function resolveActiveIndex(all: Group[], visible: Group[], activeId: number | null): number {
    if (activeId === null) return 0;
    const direct = visible.findIndex((g) => g.id === activeId);
    if (direct >= 0) return direct;
    const pos = all.findIndex((g) => g.id === activeId);
    for (let i = visible.length - 1; i >= 0; i--) {
        const group = visible[i];
        if (group && all.indexOf(group) < pos) return i;
    }
    return 0;
}

/**
 * Matching error rules set to run at the wizard ("Ausführen bei: Wizard")
 * that belong to this step. Legacy tied such a rule to the group that was
 * active when it fired; the deterministic equivalent is the group of the
 * rule's error Characteristic, or else the last group whose Characteristics
 * its condition reads. Only the "error" type blocks — a hint stays a hint.
 */
function wizardRuleErrors(
    component: Component,
    rules: RuleResult,
    step: Group,
    visible: Group[],
    activeIdx: number,
): RuleError[] {
    const groupOfAlt = new Map<string, number>();
    for (const g of component.groups) for (const c of g.characteristics) groupOfAlt.set(c.altTitle, g.id);
    const allRules = [...component.rules, ...component.ruleGroups.flatMap((rg) => rg.rules)];
    return rules.errors.filter((e) => {
        if (e.whenExecute !== 'wizard' || e.severity !== 'error') return false;
        let owner = e.characteristic ? groupOfAlt.get(e.characteristic) : undefined;
        if (owner === undefined) {
            const refs = conditionRefs(allRules.find((r) => r.id === e.ruleId)?.rule?.rules ?? []);
            const owners = refs.map((ref) => groupOfAlt.get(ref)).filter((id): id is number => id !== undefined);
            const last = visible.map((g) => g.id).filter((id) => owners.includes(id)).pop();
            owner = last ?? visible[activeIdx]?.id;
        }
        return owner === step.id;
    });
}

function conditionRefs(leaves: ReadonlyArray<{ id: string; rules?: ReadonlyArray<unknown> | undefined }>): string[] {
    return leaves.flatMap((leaf) => [
        leaf.id,
        ...conditionRefs((leaf.rules ?? []) as ReadonlyArray<{ id: string; rules?: ReadonlyArray<unknown> }>),
    ]);
}

function messageText(e: RuleError, locale: string): string {
    return typeof e.message === 'string' ? e.message : pickLocale(e.message, locale) ?? '';
}

function ruleErrorAtField(component: Component, e: RuleError, locale: string): ValidationError[] {
    if (!e.characteristic) return [];
    const char = component.groups.flatMap((g) => g.characteristics).find((c) => c.altTitle === e.characteristic);
    return char ? [{ characteristicId: char.id, altTitle: char.altTitle, code: 'rule', message: messageText(e, locale) }] : [];
}

/** Map a guard's `{ characteristic, message }` list onto inline field errors. */
function guardErrors(component: Component, list: StepGuardErrors): ValidationError[] {
    const chars = component.groups.flatMap((g) => g.characteristics);
    const out: ValidationError[] = [];
    for (const entry of list) {
        const ref = entry.characteristic;
        const char = chars.find((c) => (typeof ref === 'number' ? c.id === ref : c.altTitle === ref || String(c.id) === ref));
        if (!char) {
            console.warn('[Configento] step guard named an unknown characteristic:', ref);
            continue;
        }
        out.push({ characteristicId: char.id, altTitle: char.altTitle, code: 'guard', message: entry.message });
    }
    return out;
}

function TabsBar({
    groups,
    activeIdx,
    onSelect,
    locale,
}: {
    groups: Group[];
    activeIdx: number;
    onSelect: (idx: number, from: EventTarget | null) => void;
    locale: string | undefined;
}) {
    const { t } = useTranslation();
    return (
        <nav className="cfg-nav-tabs" role="tablist" aria-label={t('nav.tabs_aria')}>
            {groups.map((g, i) => {
                const title = pickLocale(g.title, locale) ?? t('fallback.group_title', { id: g.id });
                return (
                    <button
                        key={g.id}
                        type="button"
                        role="tab"
                        aria-selected={i === activeIdx}
                        className="cfg-nav-tabs__tab"
                        onClick={(e) => onSelect(i, e.currentTarget)}
                    >
                        {title}
                    </button>
                );
            })}
        </nav>
    );
}

function WizardBar({ idx, total }: { idx: number; total: number }) {
    const { t } = useTranslation();
    const progress = ((idx + 1) / total) * 100;
    return (
        <div className="cfg-wizard" data-configento-wizard>
            <div className="cfg-wizard__progress" aria-hidden="true">
                <div
                    className="cfg-wizard__progress-fill"
                    style={{ width: `${progress}%` }}
                />
            </div>
            <div className="cfg-wizard__step">
                {t('nav.wizard_step', { current: idx + 1, total })}
            </div>
        </div>
    );
}

function WizardActions({
    idx,
    total,
    busy,
    onBack,
    onNext,
}: {
    idx: number;
    total: number;
    /** A step guard is still deciding — no second click meanwhile. */
    busy: boolean;
    onBack: (from: EventTarget | null) => void;
    onNext: (from: EventTarget | null) => void;
}) {
    const { t } = useTranslation();
    return (
        <div className="cfg-wizard-actions">
            <button
                type="button"
                className="cfg-wizard-actions__btn"
                onClick={(e) => onBack(e.currentTarget)}
                disabled={idx === 0 || busy}
            >
                {t('nav.wizard_back')}
            </button>
            <button
                type="button"
                className="cfg-wizard-actions__btn cfg-wizard-actions__btn--primary"
                onClick={(e) => onNext(e.currentTarget)}
                disabled={idx === total - 1 || busy}
            >
                {t('nav.wizard_next')}
            </button>
        </div>
    );
}

function EmptyState() {
    const { t } = useTranslation();
    return <p className="cfg-group__empty">{t('group.empty')}</p>;
}

/**
 * Bring the wizard back into view after a step change.
 *
 * `groupWizardAutoscroll` and `groupScrollOffset` were parsed and then never
 * read — the flag did nothing. What that looked like: the Weiter button sits
 * at the bottom of a long step, so the visitor has scrolled down to reach it.
 * The next step renders shorter, nothing scrolls, and they land in the middle
 * of a form with the step bar somewhere above the viewport. They cannot see
 * which step they are on, which is the one thing a wizard is for.
 *
 * Two rules taken from the legacy storefront (app/wizard.js:updateScrollView):
 *
 *  - It only ever pulls the view UP. When the wizard already sits below the
 *    current position, scrolling would shove the page around for nothing.
 *  - `groupScrollOffset` is subtracted from the target, which is how a host
 *    page compensates for its own fixed header.
 *
 * One deliberate difference: legacy scrolled to the active GROUP, we scroll to
 * the configurator root, so the tab strip and the progress bar come along. On
 * a page with a fixed header, hitting the group's top means the step bar sits
 * just above the fold — exactly the complaint this fixes, and no merchant
 * should have to discover the right offset to work around it.
 */
function scrollWizardIntoView(from: EventTarget | null, offset: number): void {
    if (typeof document === 'undefined') return;
    // A click passes the button (root above it); the host API passes the
    // mount element (root inside it).
    const root = from instanceof Element
        ? from.closest('.cfg-root') ?? from.querySelector('.cfg-root')
        : null;
    if (!root) return;

    const top = root.getBoundingClientRect().top + window.scrollY;
    const target = Math.max(0, top - offset);
    // Legacy's guard: already above the target means the wizard is in view.
    if (window.scrollY <= target) return;

    window.scrollTo({ top: target, behavior: 'smooth' });
}
